import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateValidationError, LocalizationProvider } from '@mui/x-date-pickers';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { DatePicker as DesktopDatePicker } from '@mui/x-date-pickers/DatePicker';
import { useMediaQuery, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { fr } from 'date-fns/locale';
import { isValid } from 'date-fns/fp/isValid';

export type DatePickerValue = Date | null;

export type DatePickerProps = {
  onAccept?: (value: DatePickerValue) => void;
  handleChange: (date: DatePickerValue) => void;
  shouldDisableDate?: (date: Date) => boolean;
  value: Date | null;
  label?: string;
  maxDate?: Date;
  minDate?: Date;
  views?: ('year' | 'day' | 'month')[];
  openTo?: 'year' | 'day' | 'month';
  disabled?: boolean;
  onClose?: any;
  onError?: (error: DateValidationError, value: DatePickerValue) => void;
  ariaLabel?: string;
  errorLabel?: string;
  /** Densité du champ — `small` pour s'intégrer dans un champ de formulaire compact (FieldV2). */
  size?: 'small' | 'medium';
  /** Étire le champ sur toute la largeur disponible (utile en colonne de formulaire). */
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
};

const isValidDate = (date: Date | null) => {
  // invalid Date is a date that resolves to Invalid Date and NaN; not handled by fns
  return isValid(date) && date !== null && date.toString() !== 'Invalid Date';
};

const DatePicker = ({
  handleChange,
  onAccept,
  value,
  label = '',
  views,
  openTo,
  disabled,
  shouldDisableDate,
  onClose,
  minDate,
  maxDate,
  ariaLabel,
  errorLabel,
  onError,
  size,
  fullWidth,
  ...props
}: DatePickerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      {isMobile ? (
        <MobileDatePicker
          aria-label={ariaLabel}
          onAccept={onAccept}
          onClose={onClose}
          disabled={disabled}
          label={label}
          minDate={minDate}
          maxDate={maxDate}
          openTo={openTo ? openTo : 'day'}
          views={views ? views : ['year', 'month', 'day']}
          value={value ? value : null}
          onChange={(date: DatePickerValue) => {
            if (!isValidDate(date)) {
              return handleChange(null);
            }
            handleChange(date);
          }}
          shouldDisableDate={shouldDisableDate}
          enableAccessibleFieldDOMStructure={false}
          onError={onError}
          {...props}
          slotProps={{
            textField: {
              helperText: errorLabel,
              error: Boolean(errorLabel),
              size,
              fullWidth,
              inputProps: {
                'aria-label': ariaLabel,
              },
            },
          }}
        />
      ) : (
        <DesktopDatePicker
          aria-label={ariaLabel}
          onAccept={onAccept}
          onClose={onClose}
          disabled={disabled}
          label={label}
          minDate={minDate}
          maxDate={maxDate}
          openTo={openTo ? openTo : 'day'}
          views={views ? views : ['year', 'month', 'day']}
          value={value ? value : null}
          onChange={(date: DatePickerValue) => {
            if (!isValidDate(date)) {
              return handleChange(null);
            }
            handleChange(date);
          }}
          shouldDisableDate={shouldDisableDate}
          enableAccessibleFieldDOMStructure={false}
          onError={onError}
          {...props}
          slotProps={{
            textField: {
              helperText: errorLabel,
              error: Boolean(errorLabel),
              size,
              fullWidth,
              inputProps: {
                'aria-label': ariaLabel,
              },
            },
          }}
        />
      )}
    </LocalizationProvider>
  );
};

export default DatePicker;
export { DatePicker };
